import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ExecutionEnvironment from 'exenv';

import { NEWLY_REGISTERED_COOKIE } from 'constants/cookies';
import {
  handleSignUpModalSubmit,
  initSignUpModal,
  setSignUpModal
} from 'actions/headerfooter';
import Link from 'components/hf/HFLink';
import { CHECKOUT_URL_RE } from 'common/regex';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import LatencyTracking from 'helpers/LatencyTracking';
import { stripSpecialCharsDashReplace as strip } from 'helpers';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import SignUpModal from 'components/hf/SignUpModal';
import marketplace from 'cfg/marketplace.json';
import { toggleHeartingLoginModal } from 'actions/hearts';
import HeartLoginPrompt from 'components/common/HeartLoginPrompt';

import css from 'styles/containers/hf/6pm/footer.scss';
const { hasHearting } = marketplace;

const { vocUrl, links: { social: { instagramLink, facebookLink, twitterLink, pinterestLink, youtubeLink } } } = marketplace;

export class Footer extends Component {
  static displayName = 'Footer';
  componentDidMount() {
    const { isRemote, latencyTracking } = this.props;

    if (isRemote && !this.rootSelector) {
      /*
        "Hydrating" React Portals isn't yet supported so we get a console
        warning when serving the remote HF in dev envs.
        But worry not, nothing is actually wrong.
        https://github.com/facebook/react/issues/13097
        https://github.com/facebook/react/issues/12615
      */
      this.rootSelector = document.getElementById('martyRemoteFooter');
      this.rootSelector.innerHTML = '';
      this.forceUpdate();
    }

    this.signUpModalCheck();

    // Setup latencyTracking for init page load
    // Use latencyTracking prop for testing
    this.latencyTracking = latencyTracking || new LatencyTracking();
  }

  componentDidUpdate(prevProps) {
    const { routing: { pathname: prevPath } } = prevProps;
    const { routing: { pathname } } = this.props;
    const hasLocationChanged = prevPath !== pathname;

    this.signUpModalCheck();

    if (hasLocationChanged) {
      this.latencyTracking.triggerSoftNavigation();// Fire latency tracking for client routing
    }
  }

  signUpModalCheck = () => {
    const { routing: { pathname }, isNewlyRegistered, initSignUpModal } = this.props;
    if (isNewlyRegistered && !pathname.match(CHECKOUT_URL_RE)) {
      initSignUpModal();
    }
  };

  makeFooterNav = data => {
    const { testId } = this.context;
    const heading = data?.heading?.text;
    const { footerMenu } = data;
    return (
      <>
        {heading && <h3>{heading}</h3>}
        <ul>
          {footerMenu && footerMenu.map(({ text, link, gae }) =>
            <li key={text}>
              <Link
                data-test-id={testId(text)}
                to={link}
                onClick={() => {
                  trackEvent('TE_FOOTER_NAV', `${heading}:${gae || text}`);
                  trackLegacyEvent('Footer', strip(heading), strip(gae || text));
                }}>
                {text}
              </Link>
            </li>
          )}
        </ul>
      </>
    );
  };

  render() {
    const { isRemote, content, isSignUpModalOpen, setSignUpModal, handleSignUpModalSubmit, forceRender, toggleHeartingLoginModal = () => {}, heartLoginPrompt = { isOpen: false }, routing } = this.props;
    const { testId } = this.context;
    const {
      'footer-1': dataModal,
      'footermenu-1': data1,
      'footermenu-2': data2,
      'footermenu-3': data3,
      'footermenu-4': data4,
      'footermenu-5': data5
    } = content?.Footer?.slotData || {};

    const footer = (
      <footer className={css.footerBg} role="contentinfo">
        <h2 className={css.screenReadersOnly} data-test-id={testId('footerHeading')}>6pm Footer</h2>
        <div className={css.footerTopLinks}>
          {data1 && <div>{this.makeFooterNav(data1)}</div>}
          {data2 && <div>{this.makeFooterNav(data2)}</div>}
          {data3 && <div>{this.makeFooterNav(data3)}</div>}
          <div>
            {data4 && this.makeFooterNav(data4)}
            <p>Find us on:</p>
            <div className={css.footerSocial}>
              <a
                data-test-id={testId('footerInstagram')}
                onClick={() => {
                  trackEvent('TE_FOOTER_SOCIAL', 'instagram');
                  trackLegacyEvent('Global', 'Footer', 'Instagram');
                }}
                href={instagramLink}
                rel="noopener noreferrer"
                target="_blank"
                className={css.iconInstagram}>
                Instagram
              </a>
              <a
                data-test-id={testId('footerFacebook')}
                onClick={() => {
                  trackEvent('TE_FOOTER_SOCIAL', 'facebook');
                  trackLegacyEvent('Global', 'Footer', 'Facebook');
                }}
                href={facebookLink}
                className={css.iconFacebook}
                rel="noopener noreferrer"
                target="_blank">
                Facebook
              </a>
              <a
                data-test-id={testId('footerTwitter')}
                onClick={() => {
                  trackEvent('TE_FOOTER_SOCIAL', 'twitter');
                  trackLegacyEvent('Global', 'Footer', 'Twitter');
                }}
                href={twitterLink}
                className={css.iconTwitter}
                rel="noopener noreferrer"
                target="_blank">
                Twitter
              </a>
              <a
                data-test-id={testId('footerPinterest')}
                onClick={() => {
                  trackEvent('TE_FOOTER_SOCIAL', 'pinterest');
                  trackLegacyEvent('Global', 'Footer', 'Pinterest');
                }}
                href={pinterestLink}
                className={css.iconPinterest}
                rel="noopener noreferrer"
                target="_blank">
                Pinterest
              </a>
              <a
                data-test-id={testId('footerYouTube')}
                onClick={() => {
                  trackEvent('TE_FOOTER_SOCIAL', 'youtube');
                  trackLegacyEvent('Global', 'Footer', 'Youtube');
                }}
                href={youtubeLink}
                className={css.iconYoutube}
                rel="noopener noreferrer"
                target="_blank">
                YouTube
              </a>
            </div>
          </div>
          <div>
            {data5 && this.makeFooterNav(data5)}
            <a
              className={css.footerFeedback}
              data-test-id={testId('footerSurvey')}
              onClick={() => {
                trackEvent('TE_FOOTER_FEEDBACK');
                trackLegacyEvent('Global', 'Footer', 'Feedback');
              }}
              href={`${vocUrl}?source=footer`}
              rel="noopener noreferrer"
              target="_blank">
              <p><strong>How Do You Like Our Website?</strong></p>
              <p>We&apos;d like to get your <u>feedback</u>.</p>
            </a>
          </div>
        </div>
        <div className={css.footerLegalLinks}>
          <Link
            data-test-id={testId('footerTermsOfUse')}
            onClick={() => {
              trackEvent('TE_FOOTER_POLICIES', 'Terms of Use');
              trackLegacyEvent('Global', 'Footer', 'Terms-Of-Use');
            }}
            to="/c/terms-of-use">Terms Of Use</Link>
          <Link
            data-test-id={testId('footerPrivacyPolicy')}
            to="/c/privacy-policy"
            onClick={() => {
              trackEvent('TE_FOOTER_POLICIES', 'Privacy Policy');
              trackLegacyEvent('Global', 'Footer', 'Privacy-Policy');
            }}>Privacy Policy</Link>
          <Link
            data-test-id={testId('footerInternetBasedAds')}
            to="/c/interest-based-ads-policy"
            onClick={() => {
              trackEvent('TE_FOOTER_POLICIES', 'Interest-Based Ads');
              trackLegacyEvent('Global', 'Footer', 'Interest-Based-Ads');
            }}>Interest-Based Ads</Link>
          <a
            data-test-id={testId('footerWelg')}
            onClick={() => {
              trackEvent('TE_FOOTER_POLICIES', 'Welg');
              trackLegacyEvent('Global', 'Footer', 'Welg');
            }}
            className={css.easter}
            href="https://welg.space/"
            rel="noopener noreferrer"
            target="_blank">Welg</a>
        </div>
        <div className={css.footerIconCards}>We accept Visa, Mastercard, Discover, and American Express</div>
        <div className={css.footerBotCopy} data-test-id={testId('footerLegalCopy')}>
          <p>&copy; 2009&ndash;{new Date().getFullYear()} 6pm.com or its affiliates</p>
          <p>
            For premier service, selection, and shipping, visit&nbsp;
            <a
              data-test-id={testId('footerZappos')}
              onClick={() => trackEvent('TE_FOOTER_NAV', 'zappos')}
              href="https://www.zappos.com">Zappos.com</a>&nbsp;
            &mdash; your one-stop shop for the latest in&nbsp;
            <a
              data-test-id={testId('footerShoes')}
              onClick={() => trackEvent('TE_FOOTER_NAV', 'zappos shoes')}
              href="https://www.zappos.com/shoes">Shoes</a>,&nbsp;
            <a
              data-test-id={testId('footerSandals')}
              onClick={() => trackEvent('TE_FOOTER_NAV', 'zappos sandals')}
              href="https://www.zappos.com/Sandals">Sandals</a>,&nbsp;
            <a
              data-test-id={testId('footerDresses')}
              onClick={() => trackEvent('TE_FOOTER_NAV', 'zappos dresses')}
              href="https://www.zappos.com/Dresses">Dresses</a>,&nbsp;
            <a
              data-test-id={testId('footerJeans')}
              onClick={() => trackEvent('TE_FOOTER_NAV', 'zappos jeans')}
              href="https://www.zappos.com/Jeans">Jeans</a> and more!
          </p>
          <p>
            For luxury and designer styles, visit <a
              data-test-id={testId('footerLuxury')}
              onClick={() => trackEvent('TE_FOOTER_NAV', 'zappos luxury')}
              href="https://luxury.zappos.com/">Zappos Luxury</a>.
          </p>
        </div>
        <SignUpModal
          data={dataModal}
          isSignUpModalOpen={isSignUpModalOpen}
          setSignUpModal={setSignUpModal}
          handleSignUpModalSubmit={handleSignUpModalSubmit}/>
        {hasHearting && <HeartLoginPrompt
          isOpen={heartLoginPrompt.isOpen}
          id={heartLoginPrompt.id}
          toggleModal={toggleHeartingLoginModal}
          routing={routing}/>}
      </footer>
    );

    if (isRemote && ExecutionEnvironment.canUseDOM && this.rootSelector) {
      return createPortal(footer, this.rootSelector);
    } else if (!isRemote || forceRender) {
      return footer;
    }
    return null;
  }
}

// Placeholder just so we can run two servers at once
function mapStateToProps(state) {
  return {
    content: state.headerFooter.content,
    isSignUpModalOpen: state.headerFooter.isSignUpModalOpen,
    isNewlyRegistered: !!state.cookies[NEWLY_REGISTERED_COOKIE],
    isRemote: state.headerFooter.isRemote,
    routing: state.routing.locationBeforeTransitions,
    heartLoginPrompt: state.hearts.heartLoginPrompt
  };
}

Footer.contextTypes = {
  testId: PropTypes.func
};

const FooterConnected = connect(mapStateToProps, {
  initSignUpModal,
  setSignUpModal,
  handleSignUpModalSubmit,
  toggleHeartingLoginModal
})(Footer);

const FooterConnectedWithErrorBoundary = withErrorBoundary('Footer', FooterConnected);
export default FooterConnectedWithErrorBoundary;
